import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../axiosInstance";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import GeneralTable from "../../components/Table/GeneralTable";
import { Icon } from "@iconify/react/dist/iconify.js";
import "./ManageAssignment.scss";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import { toast } from "react-toastify";

const ManageLeads = () => {
  const [leads, setLeads] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedMember, setSelectedMember] = useState("");
  const [viewType, setViewType] = useState("TEAM");

  const salesManagerUrl = process.env.REACT_APP_SALES_MANAGER;

  const fetchUnassignedLeads = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `${salesManagerUrl}/leads/unassigned-leads`,
      );
      setLeads(response.data.data || []);
    } catch (error) {
      console.error("Error fetching unassigned leads:", error);
    } finally {
      setLoading(false);
    }
  }, [salesManagerUrl]);

  const fetchTeamAssignedLeads = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `${salesManagerUrl}/leads/assigned-leads/team`,
      );
      setLeads(response.data.data || []);
    } catch (error) {
      console.error("Error fetching team leads:", error);
      toast.error("Failed to fetch team leads");
    } finally {
      setLoading(false);
    }
  }, [salesManagerUrl]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/sales/list`,
      );
      setTeamMembers(response.data.data || []);
    } catch (error) {
      console.error("Error fetching team members:", error);
      console.log("Team Members Response:", error.response);
    }
  }, []);
  useEffect(() => {
    if (viewType === "UNASSIGNED") {
      fetchUnassignedLeads();
    } else {
      fetchTeamAssignedLeads();
    }
    fetchTeamMembers();
  }, [
    viewType,
    fetchUnassignedLeads,
    fetchTeamAssignedLeads,
    fetchTeamMembers,
  ]);

  const handleAssignLead = async (leadId) => {
    if (!selectedMember) {
      toast.info("Please select a team member");
      return;
    }

    try {
      setAssigning(true);

      await axiosInstance.put(
        `${salesManagerUrl}/leads/assign/${leadId}/employee/${selectedMember}`,
      );

      toast.success("Lead assigned successfully");
      setSelectedLead(null);
      setSelectedMember("");

      // Refresh based on view
      if (viewType === "UNASSIGNED") {
        fetchUnassignedLeads();
      } else {
        fetchTeamAssignedLeads();
      }
    } catch (error) {
      console.error("Error assigning lead:", error);
      toast.error(error?.response?.data?.message || "Failed to assign lead");
    } finally {
      setAssigning(false);
    }
  };

  // Prepare data for table with searchable text
  const tableData = leads.map((lead) => ({
    ...lead,
    searchableText: `${lead.companyName} ${lead.email} ${lead.phoneNumber} ${
      lead.companyName || ""
    } ${lead.leadSource} ${lead.leadStatus}`.toLowerCase(),
  }));

  // Define columns for the table
  const columns = [
    {
      header: "Lead",
      accessor: "companyName",
      render: (value, row) => (
        <div className="lead-identity">
          <div className="avatar avatar-square">
            {value?.charAt(0)?.toUpperCase() || "?"}
          </div>

          <div className="lead-details">
            <div className="lead-company">{value || "—"}</div>

            <div className="lead-meta">
              {row.leadCode && <span>Code: {row.leadCode}</span>}
            </div>
          </div>
        </div>
      ),
    },
    {
      header: "Contact",
      accessor: "email",
      render: (value, row) => (
        <div className="contact-info">
          <div className="contact-item">
            <Icon icon="ic:baseline-email" className="contact-icon" />
            <span className="contact-text">{value}</span>
          </div>
          <div className="contact-item">
            <Icon icon="ic:baseline-phone" className="contact-icon" />
            <span className="contact-text">{row.phoneNumber}</span>
          </div>
        </div>
      ),
    },
    {
      header: "Company",
      accessor: "companyName",
      render: (value, row) => (
        <div className="company-info">
          <div className="company-name-text">{value || "—"}</div>
          {row.industry && <div className="industry-text">{row.industry}</div>}
        </div>
      ),
    },
    {
      header: "Source",
      accessor: "leadSource",
      render: (value) => {
        const sourceClass = {
          CAMPAIGN: "source-campaign",
          REFERRAL: "source-referral",
          WEBSITE: "source-website",
          SOCIAL: "source-social",
        };
        return (
          <span className={`badge ${sourceClass[value] || "badge-default"}`}>
            {value}
          </span>
        );
      },
    },
    {
      header: "Status",
      accessor: "leadStatus",
      render: (value) => {
        const statusClass = {
          AVAILABLE: "status-available",
          CONTACTED: "status-contacted",
          QUALIFIED: "status-qualified",
          CONVERTED: "status-converted",
        };
        return (
          <span className={`badge ${statusClass[value] || "badge-default"}`}>
            {value}
          </span>
        );
      },
    },
    {
      header: "Score",
      accessor: "leadScore",
      render: (value) => (
        <div className="score-display">
          <Icon icon="ic:baseline-star" className="score-icon" />
          <span className="score-value">{value || 0}</span>
        </div>
      ),
    },
    {
      header: "Category",
      accessor: "category",
      render: (value) => <span className="category-badge">{value}</span>,
    },
    {
      header: "Actions",
      accessor: "id",
      render: (value, row) => {
        const assigned = !!row.assignedToId;

        return (
          <div className="actions-cell">
            {/* When assigning / reassigning */}
            {selectedLead === value ? (
              <div className="assignment-section">
                {assigned && (
                  <div className="current-assignee">
                    <Icon icon="ic:baseline-person" />
                    <span>
                      Currently assigned to{" "}
                      <strong>{row.assignedToName}</strong>
                    </span>
                  </div>
                )}

                <select
                  value={selectedMember}
                  onChange={(e) => setSelectedMember(e.target.value)}
                  className="member-select"
                >
                  <option value="">Select Team Member</option>
                  {teamMembers.map((member) => (
                    <option key={member.empId} value={member.empId}>
                      {member.firstName} {member.lastName}
                    </option>
                  ))}
                </select>

                <div className="button-group">
                  <ButtonWithIcon
                    text={
                      assigning ? "Saving..." : assigned ? "Reassign" : "Assign"
                    }
                    onClick={() => handleAssignLead(value)}
                    disabled={assigning || !selectedMember}
                    className="btn-confirm"
                  />
                  <ButtonWithIcon
                    text="Cancel"
                    onClick={() => {
                      setSelectedLead(null);
                      setSelectedMember("");
                    }}
                    className="btn-cancel"
                  />
                </div>
              </div>
            ) : (
              <>
                {assigned && (
                  <div className="assigned-info">
                    <Icon icon="ic:baseline-person" />
                    <span className="assigned-name">{row.assignedToName}</span>
                  </div>
                )}

                <ButtonWithIcon
                  icon="ic:baseline-person-add"
                  text={assigned ? "Reassign " : "Assign "}
                  onClick={() => setSelectedLead(value)}
                  className={assigned ? "btn-reassign" : "btn-assign"}
                />
              </>
            )}
          </div>
        );
      },
    },
  ];

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="manage-assignment-container">
      <div className="assignment-toggle">
        <button
          className={`toggle-btn ${viewType === "TEAM" ? "active" : ""}`}
          onClick={() => setViewType("TEAM")}
        >
          Team Leads
        </button>
        <button
          className={`toggle-btn ${viewType === "UNASSIGNED" ? "active" : ""}`}
          onClick={() => setViewType("UNASSIGNED")}
        >
          Unassigned Leads
        </button>
      </div>

      <GeneralTable
        columns={columns}
        data={tableData}
        title={
          viewType === "UNASSIGNED"
            ? "Manage Unassigned Leads"
            : "Team Assigned Leads"
        }
        initialPageSize={10}
        showPagination={true}
      />
    </div>
  );
};

export default ManageLeads;
